import { StandardEvents } from '@shopify/events';

/**
 * Granel Vital — selector de presentación de la ficha de producto.
 *
 * Contrato pixel-safe: no resuelve variantes ni toca el carrito. Cada clic se
 * reenvía al input del variant picker nativo, que es quien actualiza precio,
 * disponibilidad, el ID que viaja al carrito y el evento
 * `shopify:product:select`. Aquí solo vive la presentación visual.
 *
 * Si no encuentra el input nativo correspondiente, se retira: devuelve la
 * visibilidad al selector del tema en lugar de dejar al cliente con un control
 * que no hace nada.
 */
class GvPresentationPicker extends HTMLElement {
  connectedCallback() {
    this.#scope = this.closest('.shopify-section, dialog');
    this.#options = Array.from(this.querySelectorAll('.gv-picker__option'));

    if (!this.#options.length || !this.#findNativeInput(this.#options[0])) return;

    // Solo se oculta el selector nativo cuando se comprobó que hay con qué
    // reemplazarlo.
    this.#scope?.classList.add('gv-picker-active');

    this.addEventListener('click', this.#handleClick);
    this.addEventListener('keydown', this.#handleKeydown);
    this.#scope?.addEventListener(StandardEvents.productSelect, this.#sync);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeydown);
    this.#scope?.removeEventListener(StandardEvents.productSelect, this.#sync);
  }

  /** @type {Element | null} */
  #scope = null;
  /** @type {HTMLElement[]} */
  #options = [];

  /**
   * @param {Element} option
   * @returns {HTMLInputElement | null}
   */
  #findNativeInput(option) {
    if (!this.#scope) return null;

    const valueId = option.getAttribute('data-option-value-id');
    const byId = valueId
      ? this.#scope.querySelector(`input[data-option-value-id="${CSS.escape(valueId)}"]`)
      : null;
    if (byId instanceof HTMLInputElement) return byId;

    // Respaldo por nombre de la opción, para temas donde el id no viaja al DOM.
    const value = option.getAttribute('data-option-value');
    if (!value) return null;

    const candidates = this.#scope.querySelectorAll('.variant-option input[type="radio"], .variant-option option');
    for (const candidate of candidates) {
      if (candidate instanceof HTMLInputElement && candidate.value === value) return candidate;
    }
    return null;
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const option = target.closest('.gv-picker__option');
    if (!(option instanceof HTMLElement)) return;

    const input = this.#findNativeInput(option);
    if (!input) return;

    // Marca optimista: el tema tarda una petición en devolver el HTML nuevo, y
    // sin esto el clic se siente muerto durante ese tiempo.
    this.#mark(option);

    input.checked = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));
    input.click();
  };

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

    const current = this.#options.findIndex((option) => option.classList.contains('is-selected'));
    if (current === -1) return;

    const delta = event.key === 'ArrowDown' ? 1 : -1;
    const next = this.#options[(current + delta + this.#options.length) % this.#options.length];
    if (!next) return;

    event.preventDefault();
    next.focus();
    next.click();
  };

  /** Re-sincroniza con el input nativo tras un cambio de variante. */
  #sync = () => {
    requestAnimationFrame(() => {
      for (const option of this.#options) {
        const input = this.#findNativeInput(option);
        if (input?.checked) this.#mark(option);
      }
    });
  };

  /** @param {HTMLElement} option */
  #mark(option) {
    for (const other of this.#options) {
      const selected = other === option;
      other.classList.toggle('is-selected', selected);
      other.setAttribute('aria-checked', String(selected));
    }
  }
}

if (!customElements.get('gv-presentation-picker')) {
  customElements.define('gv-presentation-picker', GvPresentationPicker);
}
